import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';

const getTeamLabel = (team) => team?.team_name || team?.team || '-';
const getWon = (team) => team?.wins ?? team?.won ?? 0;
const getDrawn = (team) => team?.draws ?? team?.drawn ?? 0;
const getLost = (team) => team?.losses ?? team?.lost ?? 0;

const normalizeName = (name = '') => String(name).trim().toLowerCase();

const getHeadToHead = (matches, teamA, teamB) => {
  const a = normalizeName(teamA);
  const b = normalizeName(teamB);
  const match = matches.find((m) => {
    const h = normalizeName(m.home_team);
    const aw = normalizeName(m.away_team);
    const direct = h === a && aw === b;
    const reverse = h === b && aw === a;
    return (direct || reverse) && m.status === 'finished' && m.home_score !== null && m.away_score !== null;
  });

  if (!match) {
    return { score: 'TBD', winner: null };
  }

  const direct = normalizeName(match.home_team) === a;
  const score = direct
    ? `${match.home_score} - ${match.away_score}`
    : `${match.away_score} - ${match.home_score}`;

  if (match.home_score === match.away_score) {
    return { score, winner: null };
  }

  const winner = match.home_score > match.away_score ? match.home_team : match.away_team;
  return { score, winner };
};

const buildKnockout = (teams, matches) => {
  if (!teams || teams.length < 2) {
    return null;
  }

  const seeded = [...teams].sort((a, b) => (a.position || 999) - (b.position || 999));
  let bracketTeams = [];
  if (seeded.length >= 8) {
    bracketTeams = seeded.slice(0, 8);
  } else if (seeded.length >= 4) {
    bracketTeams = seeded.slice(0, 4);
  } else {
    bracketTeams = seeded.slice(0, 2);
  }

  const label = (team) => `${team.position}. ${getTeamLabel(team)}`;
  const winnerName = (headToHead, fallbackTeam) => headToHead.winner || getTeamLabel(fallbackTeam);

  if (bracketTeams.length === 8) {
    const qf = [
      [bracketTeams[0], bracketTeams[7]],
      [bracketTeams[3], bracketTeams[4]],
      [bracketTeams[1], bracketTeams[6]],
      [bracketTeams[2], bracketTeams[5]],
    ].map(([home, away]) => {
      const result = getHeadToHead(matches, getTeamLabel(home), getTeamLabel(away));
      return { home: label(home), away: label(away), score: result.score, winner: winnerName(result, home) };
    });

    const sf = [
      [qf[0].winner, qf[1].winner],
      [qf[2].winner, qf[3].winner],
    ].map(([home, away]) => {
      const result = getHeadToHead(matches, home, away);
      return { home, away, score: result.score, winner: result.winner || home };
    });

    const final = {
      home: sf[0].winner,
      away: sf[1].winner,
      ...(() => {
        const result = getHeadToHead(matches, sf[0].winner, sf[1].winner);
        return { score: result.score, winner: result.winner || sf[0].winner };
      })(),
    };

    return {
      rounds: [
        { title: 'Babak Penyisihan (8 Besar)', matches: qf },
        { title: 'Semi Final', matches: sf },
        { title: 'Final', matches: [final] },
      ],
      champion: final.winner,
    };
  }

  if (bracketTeams.length === 4) {
    const sf = [
      [bracketTeams[0], bracketTeams[3]],
      [bracketTeams[1], bracketTeams[2]],
    ].map(([home, away]) => {
      const result = getHeadToHead(matches, getTeamLabel(home), getTeamLabel(away));
      return { home: label(home), away: label(away), score: result.score, winner: winnerName(result, home) };
    });

    const final = {
      home: sf[0].winner,
      away: sf[1].winner,
      ...(() => {
        const result = getHeadToHead(matches, sf[0].winner, sf[1].winner);
        return { score: result.score, winner: result.winner || sf[0].winner };
      })(),
    };

    return {
      rounds: [
        { title: 'Babak Penyisihan', matches: sf },
        { title: 'Final', matches: [final] },
      ],
      champion: final.winner,
    };
  }

  const final = {
    home: label(bracketTeams[0]),
    away: label(bracketTeams[1]),
    ...(() => {
      const result = getHeadToHead(matches, getTeamLabel(bracketTeams[0]), getTeamLabel(bracketTeams[1]));
      return { score: result.score, winner: result.winner || getTeamLabel(bracketTeams[0]) };
    })(),
  };

  return {
    rounds: [{ title: 'Final', matches: [final] }],
    champion: final.winner,
  };
};

const Draw = () => {
  const [events, setEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState('');
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('U-8');
  const [standings, setStandings] = useState([]);
  const [matches, setMatches] = useState([]);
  const [bracket, setBracket] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadEventsAndCategories();
  }, []);

  useEffect(() => {
    if (selectedCategory) {
      loadData();
    }
  }, [selectedCategory, selectedEventId]);

  const loadEventsAndCategories = async () => {
    try {
      const response = await apiService.getPublicEvents();
      const eventList = response.data || [];
      setEvents(eventList);
      if (eventList.length > 0) {
        setSelectedEventId(eventList[0].id);
      }

      const allCats = new Set();
      eventList.forEach(event => {
        event.categories?.forEach(cat => allCats.add(cat));
      });
      const uniqueCats = Array.from(allCats).sort();
      setCategories(uniqueCats.length > 0 ? uniqueCats : ['U-8', 'U-10', 'U-12']);
      if (uniqueCats.length > 0) setSelectedCategory(uniqueCats[0]);
    } catch (error) {
      console.error('Error loading categories:', error);
      setCategories(['U-8', 'U-10', 'U-12']);
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [standingsResponse, matchesResponse] = await Promise.all([
        apiService.getStandings(selectedCategory),
        apiService.getMatches(undefined, selectedCategory),
      ]);

      const allStandings = standingsResponse.data || [];
      const allMatches = matchesResponse.data || [];

      const scopedStandings = selectedEventId
        ? allStandings.filter((team) => {
            const t = normalizeName(getTeamLabel(team));
            return allMatches.some((m) => m.event_id === selectedEventId && (normalizeName(m.home_team) === t || normalizeName(m.away_team) === t));
          })
        : allStandings;

      const scopedMatches = selectedEventId
        ? allMatches.filter((m) => m.event_id === selectedEventId)
        : allMatches;

      setStandings(scopedStandings);
      setMatches(scopedMatches);
      setBracket(buildKnockout(scopedStandings, scopedMatches));
    } catch (error) {
      console.error('Error loading standings:', error);
      setStandings([]);
      setMatches([]);
      setBracket(null);
    } finally {
      setLoading(false);
    }
  };

  const getRankColor = (position) => {
    if (position === 1) return 'bg-yellow-100 border-yellow-400';
    if (position === 2) return 'bg-slate-100 border-slate-400';
    if (position === 3) return 'bg-orange-100 border-orange-400';
    return 'bg-gray-50 border-gray-300';
  };

  const getMedalIcon = (position) => {
    if (position === 1) return '🥇';
    if (position === 2) return '🥈';
    if (position === 3) return '🥉';
    return `${position}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">🏆 Tournament Draw</h1>
          <p className="text-gray-600">Lihat hasil turnamen dan pemenang per kategori</p>
        </div>

        {/* Category Selector */}
        <div className="flex justify-center gap-3 mb-4 flex-wrap">
          <select
            value={selectedEventId}
            onChange={(e) => setSelectedEventId(e.target.value)}
            className="px-4 py-2 border-2 border-gray-300 rounded-lg bg-white"
          >
            {events.map((event) => (
              <option key={event.id} value={event.id}>{event.name}</option>
            ))}
          </select>
        </div>

        <div className="flex justify-center gap-3 mb-8 flex-wrap">
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              className={`px-6 py-2 rounded-full font-semibold transition-all ${
                selectedCategory === cat
                  ? 'bg-blue-600 text-white shadow-lg'
                  : 'bg-white text-gray-700 border-2 border-gray-300 hover:border-blue-400'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Loading State */}
        {loading && (
          <div className="text-center py-12">
            <div className="text-lg text-gray-600">Memuat data...</div>
          </div>
        )}

        {/* Knockout Bracket */}
        {!loading && bracket && (
          <div className="mb-10 bg-white rounded-xl shadow p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Bagan Knockout</h2>
            <div className="overflow-x-auto">
              <div className="min-w-[920px] grid grid-cols-3 gap-5 items-start">
                {bracket.rounds.map((round) => (
                  <div key={round.title}>
                    <h3 className="text-sm uppercase tracking-wide font-bold text-gray-700 mb-3">{round.title}</h3>
                    <div className="space-y-3">
                      {round.matches.map((match, idx) => (
                        <div key={`${round.title}-${idx}`} className="border-2 border-gray-300 rounded-lg p-3 bg-gray-50">
                          <p className="font-semibold text-gray-900 text-sm">{match.home}</p>
                          <p className="font-semibold text-gray-900 text-sm">{match.away}</p>
                          <p className="text-xs text-gray-600 mt-1">Skor: {match.score}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="mt-5 inline-flex items-center gap-2 px-4 py-2 bg-yellow-100 rounded-full border border-yellow-300">
              <span className="text-lg">👑</span>
              <span className="font-semibold text-gray-900">Pemenang Sementara: {bracket.champion || 'TBD'}</span>
            </div>
          </div>
        )}

        {/* Standings Grid */}
        {!loading && standings.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Top 3 Winners */}
            <div className="md:col-span-2">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">🎯 Pemenang Utama</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {standings.slice(0, 3).map((team, idx) => (
                  <div
                    key={idx}
                    className={`p-6 rounded-lg border-2 ${getRankColor(team.position)} shadow-md`}
                  >
                    <div className="text-center">
                      <div className="text-5xl mb-2">{getMedalIcon(team.position)}</div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{getTeamLabel(team)}</h3>
                      <div className="space-y-1 text-sm">
                        <p className="text-gray-700">
                          <span className="font-semibold">Poin:</span> {team.points}
                        </p>
                        <p className="text-gray-600">
                          <span className="font-semibold">Main:</span> {team.played} | <span className="font-semibold">Menang:</span> {getWon(team)}
                        </p>
                        <p className="text-gray-600">
                          <span className="font-semibold">Gol:</span> {team.goals_for}:{team.goals_against}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Full Standings Table */}
            <div className="md:col-span-2">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">📊 Tabel Lengkap</h2>
              <div className="overflow-x-auto rounded-lg shadow">
                <table className="w-full text-sm">
                  <thead className="bg-blue-600 text-white">
                    <tr>
                      <th className="px-4 py-3 text-left">Posisi</th>
                      <th className="px-4 py-3 text-left">Tim</th>
                      <th className="px-4 py-3 text-center">Main</th>
                      <th className="px-4 py-3 text-center">Menang</th>
                      <th className="px-4 py-3 text-center">Imbang</th>
                      <th className="px-4 py-3 text-center">Kalah</th>
                      <th className="px-4 py-3 text-center">Gol</th>
                      <th className="px-4 py-3 text-center">Poin</th>
                    </tr>
                  </thead>
                  <tbody>
                    {standings.map((team, idx) => (
                      <tr
                        key={idx}
                        className={`border-b transition-colors hover:bg-blue-50 ${
                          idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                        } ${team.position <= 3 ? 'font-semibold' : ''}`}
                      >
                        <td className="px-4 py-3 text-center font-bold text-lg">
                          {team.position <= 3 ? getMedalIcon(team.position) : team.position}
                        </td>
                        <td className="px-4 py-3">{getTeamLabel(team)}</td>
                        <td className="px-4 py-3 text-center">{team.played}</td>
                        <td className="px-4 py-3 text-center text-green-600 font-semibold">{getWon(team)}</td>
                        <td className="px-4 py-3 text-center text-yellow-600 font-semibold">{getDrawn(team)}</td>
                        <td className="px-4 py-3 text-center text-red-600 font-semibold">{getLost(team)}</td>
                        <td className="px-4 py-3 text-center">
                          {team.goals_for}:{team.goals_against}
                        </td>
                        <td className="px-4 py-3 text-center font-bold text-blue-600 text-lg">
                          {team.points}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        ) : (
          !loading && (
            <div className="text-center py-12 bg-white rounded-lg shadow">
              <p className="text-gray-600 text-lg">
                Belum ada data untuk kategori {selectedCategory}
              </p>
            </div>
          )
        )}

        {/* Info Box */}
        <div className="mt-10 p-6 bg-blue-50 border border-blue-200 rounded-lg">
          <h3 className="font-semibold text-gray-900 mb-2">ℹ️ Informasi</h3>
          <ul className="text-sm text-gray-700 space-y-1">
            <li>• <strong>Poin:</strong> Menang = 3 poin, Imbang = 1 poin, Kalah = 0 poin</li>
            <li>• <strong>Ranking:</strong> Berdasarkan poin, selisih gol, dan gol yang dicetak</li>
            <li>• <strong>Kategori:</strong> Pilih kategori umur di atas untuk melihat hasil lengkap</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default Draw;
